import React from 'react';
import { useAuth0Context } from '../auth/auth0/Auth0Context';
import { useAppConfigContext } from './AppConfigProvider';
import { UniversalAuthUser, useUser } from '../hooks/useUser';

interface HasDataReportingPlusResponse {
    hasDataReportingPlus: boolean;
}

type LicenseMap = Record<string, boolean>;

/**
 * Context that checks whether the current tenant has the DataReportingPlus license
 * by calling the bbdata-roles-and-permissions API. The result is used to gate
 * access to Custom Reports in the UI.
 */

const TenantLicenseReactContext = React.createContext<LicenseMap>({});

export const useTenantLicenseContext = (): LicenseMap =>
    React.useContext(TenantLicenseReactContext);

export const TenantLicenseProvider: React.FunctionComponent = ({
    children,
}) => {
    const { accessToken } = useAuth0Context();
    const { api } = useAppConfigContext();
    const user = useUser();
    const tenantId = user?.isUniversalUser()
        ? (user as UniversalAuthUser).tenantId
        : null;

    const [licenses, setLicenses] = React.useState<LicenseMap>({});

    React.useEffect(() => {
        if (!tenantId || !accessToken || !api?.userManagement) {
            return;
        }

        let cancelled = false;

        const fetchLicense = async () => {
            const response = await fetch(
                encodeURI(
                    `${api.userManagement}/v1/tenants/${tenantId}/hasDataReportingPlus`,
                ),
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                },
            );

            if (!response.ok || cancelled) {
                return;
            }

            const data: HasDataReportingPlusResponse = await response.json();

            if (!cancelled) {
                setLicenses({
                    DataReportingPlus: data.hasDataReportingPlus,
                });
            }
        };

        fetchLicense();

        return () => {
            cancelled = true;
        };
    }, [tenantId, accessToken, api?.userManagement]);

    return (
        <TenantLicenseReactContext.Provider value={licenses}>
            {children}
        </TenantLicenseReactContext.Provider>
    );
};
